import { IGif } from "services/types";
import { createHlsUrl } from "utils/urls";

const MAX_THREAD_COUNT = 3;

class UrlPrefetcher {
  private urlQueue: string[] = [];
  private activeThreadCount: number = 0;

  public addUrl(url: string) {
    if (!this.urlQueue.includes(url)) {
      this.urlQueue.push(url);
    }
  }

  public run() {
    this.prefetchUrls();
  }

  private prefetchUrls() {
    if (!this.urlQueue.length || this.activeThreadCount >= MAX_THREAD_COUNT) {
      return;
    }

    const urls = this.urlQueue.splice(0, MAX_THREAD_COUNT - this.activeThreadCount);
    if (!urls.length) {
      return;
    }

    urls.forEach(url => {
      this.activeThreadCount++;

      this.prefetchUrl(url).catch(() => null)
        .finally(() => {
          this.activeThreadCount--;
          this.prefetchUrls();
        });
    });
  }

  private async prefetchUrl(url: string): Promise<Response | void> {
    if (url.endsWith(".m3u8")) {
      const response = await fetch(url);
      if (!response || !response.ok) {
        return;
      }

      const playlist = await response.text();
      const matches = playlist.match(/EXT-X-MAP:URI="(https?:\/\/[^\s]+?)"/i);
      if (matches && matches[1]) {
        this.addUrl(matches[1]);
      }

      return;
    }

    return fetch(url, { method: "HEAD" });
  }
}

class GifPreloader {
  private prefetcher: UrlPrefetcher;

  public constructor() {
    this.prefetcher = new UrlPrefetcher();
  }

  public preload(gifs: IGif[]) {
    gifs.forEach(gif => {
      const urls = this.getFileUrls(gif);
      urls.forEach(url => this.prefetcher.addUrl(url));
    });

    this.prefetcher.run();
  }

  private getFileUrls(gif: IGif) {
    const qualities = ["sd", "hd"];
    const urls: string[] = gif.urls.thumbnail ? [gif.urls.thumbnail] : [];

    if (gif.hls) {
      qualities.forEach(quality => {
        urls.push(createHlsUrl(gif.id, quality));
      });
    } else {
      (qualities as Array<keyof typeof gif.urls>).forEach(value => {
        if (gif.urls[value]) {
          urls.push(gif.urls[value]);
        }
      });
    }

    return urls;
  }
}

export default new GifPreloader();

